import { Color } from './java/Color';
import { Graphics2D } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { Random } from './java/Random';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { ID } from './ID';
import { Game } from './Game';
import { TrailPool } from './TrailPool';
import { EnemyBossBullet } from './EnemyBossBullet';

/**
 * Small bouncing enemy spawned by BossSwarm. Some shoot bullets.
 * Uses ID.BasicEnemy for collision (deals standard damage).
 */
export class SwarmMinion extends GameObject {

  private static readonly SIZE = 20;
  private static readonly FILL: Color = new Color(245, 170, 60);
  private static readonly GLOW: Color = new Color(245, 170, 60, 30);
  private static readonly TRAIL_COLOR: Color = new Color(245, 170, 60);

  private handler: Handler;
  private r: Random = new Random();
  private trailTick = 0;
  private readonly boundsRect: Rectangle = new Rectangle();
  private canShoot: boolean;
  private shootCooldown: number;
  private rotation = 0;

  constructor(x: number, y: number, handler: Handler, canShoot: boolean) {
    super(x, y, ID.BasicEnemy);
    this.handler = handler;
    this.canShoot = canShoot;
    this.shootCooldown = 120 + this.r.nextInt(80);

    // Random bounce direction
    const speed = 3 + this.r.nextFloat() * 3;
    const angle = this.r.nextFloat() * (Math.PI * 2);
    this.velX = Math.cos(angle) * speed;
    this.velY = Math.sin(angle) * speed;
  }

  getBounds(): Rectangle {
    this.boundsRect.x = Math.trunc(this.x);
    this.boundsRect.y = Math.trunc(this.y);
    this.boundsRect.width = SwarmMinion.SIZE;
    this.boundsRect.height = SwarmMinion.SIZE;
    return this.boundsRect;
  }

  tick(): void {
    const SIZE = SwarmMinion.SIZE;

    this.x += this.velX;
    this.y += this.velY;
    this.rotation += 0.08;

    // Bounce
    if (this.y <= 0 || this.y >= Game.HEIGHT - SIZE) {
      this.velY *= -1;
      if (this.y <= 0) this.y = 0;
      if (this.y >= Game.HEIGHT - SIZE) this.y = Game.HEIGHT - SIZE;
    }
    if (this.x <= 0 || this.x >= Game.WIDTH - SIZE) {
      this.velX *= -1;
      if (this.x <= 0) this.x = 0;
      if (this.x >= Game.WIDTH - SIZE) this.x = Game.WIDTH - SIZE;
    }

    if (++this.trailTick % 5 === 0) {
      TrailPool.add(this.x, this.y, SwarmMinion.TRAIL_COLOR, SIZE, SIZE, 0.03, TrailPool.SHAPE_DIAMOND);
    }

    // Shoot
    if (this.canShoot) {
      this.shootCooldown--;
      if (this.shootCooldown <= 0) {
        this.shootCooldown = 140 + this.r.nextInt(60);
        // Find player and shoot toward them
        for (let i = 0; i < this.handler.getObjects().length; i++) {
          if (this.handler.getObjects()[i].getId() === ID.Player) {
            const player = this.handler.getObjects()[i];
            const dx = player.getX() + 24 - (this.x + Math.trunc(SIZE / 2));
            const dy = player.getY() + 24 - (this.y + Math.trunc(SIZE / 2));
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > 0) {
              const speed = 3.5;
              this.handler.addObject(new EnemyBossBullet(
                Math.trunc(this.x + Math.trunc(SIZE / 2)), Math.trunc(this.y + Math.trunc(SIZE / 2)), ID.BasicEnemy, this.handler,
                dx / dist * speed, dy / dist * speed));
            }
            break;
          }
        }
      }
    }
  }

  render(g: Graphics2D): void {
    const SIZE = SwarmMinion.SIZE;
    const g2 = g;
    const cx = Math.trunc(this.x) + Math.trunc(SIZE / 2);
    const cy = Math.trunc(this.y) + Math.trunc(SIZE / 2);
    const half = Math.trunc(SIZE / 2);

    // Rotated diamond (like FastEnemy but smaller)
    g2.setColor(SwarmMinion.GLOW);
    this.drawDiamond(g2, cx, cy, half + 3);
    g2.setColor(SwarmMinion.FILL);
    this.drawDiamond(g2, cx, cy, half);

    // Shoot indicator
    if (this.canShoot) {
      g2.setColor(new Color(255, 255, 255, 60));
      g2.fillOval(cx - 2, cy - 2, 4, 4);
    }
  }

  private drawDiamond(g: Graphics2D, cx: number, cy: number, r: number): void {
    const xp: number[] = new Array<number>(4).fill(0);
    const yp: number[] = new Array<number>(4).fill(0);
    for (let i = 0; i < 4; i++) {
      const a = this.rotation + i * (Math.PI / 2);
      xp[i] = cx + Math.trunc(Math.cos(a) * r);
      yp[i] = cy + Math.trunc(Math.sin(a) * r);
    }
    g.fillPolygon(xp, yp, 4);
  }
}
